import { useEffect, useMemo, useState } from 'react';
import {
  Line,
  LineChart,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ReferenceLine,
  ResponsiveContainer,
} from 'recharts';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { Card, Button, Title, Text } from '@nimbus-ds/components';
import {
  customEventsService,
  type CustomEventsTimelineResponse,
} from '@/api/customEvents';

type Metric = 'total_occurrences' | 'unique_sessions' | 'conversion_lift';
type Smoothing = 'raw' | 'ma3';

const METRIC_LABELS: Record<Metric, string> = {
  total_occurrences: 'Occurrences',
  unique_sessions: 'Sessions',
  conversion_lift: 'Lift',
};

const SMOOTHING_LABELS: Record<Smoothing, string> = {
  raw: 'Daily',
  ma3: '3d avg',
};

interface CustomEventsTimelineProps {
  storeId: string;
  windowDays?: number;
  /** event_ids to hide from the chart (e.g., archived events when toggle is off) */
  hideEventIds?: Set<string>;
}

const DEFAULT_WINDOW_DAYS = 30;

const LINE_COLORS = [
  '#3b82f6', // blue
  '#10b981', // emerald
  '#f59e0b', // amber
  '#8b5cf6', // violet
  '#ef4444', // red
  '#06b6d4', // cyan
  '#ec4899', // pink
  '#84cc16', // lime
  '#6366f1', // indigo
  '#f97316', // orange
];

export function CustomEventsTimeline({ storeId, windowDays = DEFAULT_WINDOW_DAYS, hideEventIds }: CustomEventsTimelineProps) {
  const [data, setData] = useState<CustomEventsTimelineResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [metric, setMetric] = useState<Metric>('total_occurrences');
  const [smoothing, setSmoothing] = useState<Smoothing>('raw');
  const [hiddenEvents, setHiddenEvents] = useState<Set<string>>(new Set());

  const toggleEvent = (eventId: string) => {
    setHiddenEvents((prev) => {
      const next = new Set(prev);
      if (next.has(eventId)) next.delete(eventId);
      else next.add(eventId);
      return next;
    });
  };

  useEffect(() => {
    if (!storeId) return;
    let cancelled = false;

    setIsLoading(true);
    setError(null);

    customEventsService
      .getTimeline(storeId, windowDays)
      .then((res) => {
        if (cancelled) return;
        setData(res);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('Failed to load custom events timeline:', err);
        setError('Failed to load timeline');
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [storeId, windowDays]);

  const { chartData, eventDefs } = useMemo(() => {
    if (!data) return { chartData: [], eventDefs: [] };

    const defs = (data.events_index || [])
      .filter((ev) => !hideEventIds?.has(ev.event_id))
      .map((ev, i) => ({
        ...ev,
        color: LINE_COLORS[i % LINE_COLORS.length],
      }));

    const points = (data.series || []).map((point) => {
      const row: Record<string, string | number | null> = {
        date: point.date,
        label: format(new Date(point.date + 'T00:00:00Z'), 'dd MMM', { locale: es }),
      };
      for (const def of defs) {
        const ev = point.events?.[def.event_id];
        if (!ev) {
          row[def.event_id] = metric === 'conversion_lift' ? null : 0;
          continue;
        }
        if (metric === 'conversion_lift') {
          row[def.event_id] = ev.conversion_lift != null ? +(ev.conversion_lift * 100).toFixed(2) : null;
        } else {
          row[def.event_id] = ev[metric] ?? 0;
        }
      }
      return row;
    });

    if (smoothing === 'ma3' && points.length) {
      const window = 3;
      const smoothed = points.map((row, i) => {
        const out: Record<string, string | number | null> = {
          date: row.date,
          label: row.label,
        };
        for (const def of defs) {
          let sum = 0;
          let count = 0;
          for (let k = Math.max(0, i - window + 1); k <= i; k++) {
            const v = points[k][def.event_id];
            if (typeof v === 'number') {
              sum += v;
              count += 1;
            }
          }
          if (count === 0) {
            out[def.event_id] = metric === 'conversion_lift' ? null : 0;
          } else {
            const avg = sum / count;
            out[def.event_id] = metric === 'conversion_lift'
              ? +avg.toFixed(2)
              : Math.round(avg * 100) / 100;
          }
        }
        return out;
      });
      return { chartData: smoothed, eventDefs: defs };
    }

    return { chartData: points, eventDefs: defs };
  }, [data, metric, smoothing, hideEventIds]);

  if (isLoading) {
    return (
      <Card>
        <Card.Header>
          <Title as="h3">Custom Events Timeline</Title>
          <Text>Per-day breakdown across the selected window</Text>
        </Card.Header>
        <Card.Body>
          <div className="h-[280px] w-full animate-pulse rounded-md bg-muted" />
        </Card.Body>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <Card.Header>
          <Title as="h3">Custom Events Timeline</Title>
        </Card.Header>
        <Card.Body>
          <p className="text-sm text-red-600">{error}</p>
        </Card.Body>
      </Card>
    );
  }

  if (!chartData.length || !eventDefs.length) {
    return (
      <Card>
        <Card.Header>
          <Title as="h3">Custom Events Timeline</Title>
          <Text>Per-day breakdown across the selected window</Text>
        </Card.Header>
        <Card.Body>
          <Text className="py-8 text-center">
            No custom events recorded in this window yet.
          </Text>
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card>
      <Card.Header>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
          <div>
            <Title as="h3">Custom Events Timeline</Title>
            <Text>
              {eventDefs.length} event{eventDefs.length === 1 ? '' : 's'} · last {windowDays} days
            </Text>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex gap-1 rounded-md border p-0.5">
              {(Object.keys(SMOOTHING_LABELS) as Smoothing[]).map((s) => (
                <Button
                  key={s}
                  appearance={smoothing === s ? 'primary' : 'transparent'}
                  size="small"
                  onClick={() => setSmoothing(s)}
                >
                  {SMOOTHING_LABELS[s]}
                </Button>
              ))}
            </div>
            <div className="flex gap-1 rounded-md border p-0.5">
              {(Object.keys(METRIC_LABELS) as Metric[]).map((m) => (
                <Button
                  key={m}
                  appearance={metric === m ? 'primary' : 'transparent'}
                  size="small"
                  onClick={() => setMetric(m)}
                >
                  {METRIC_LABELS[m]}
                </Button>
              ))}
            </div>
          </div>
        </div>
      </Card.Header>
      <Card.Body>
        <ResponsiveContainer width="100%" height={320}>
          <LineChart data={chartData} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis dataKey="label" tick={{ fontSize: 12 }} />
            <YAxis
              tick={{ fontSize: 12 }}
              allowDecimals={metric === 'conversion_lift'}
              domain={metric === 'conversion_lift' ? ['auto', 'auto'] : [0, 'auto']}
              tickFormatter={(v) => (metric === 'conversion_lift' ? `${v}%` : `${v}`)}
            />
            {metric === 'conversion_lift' && (
              <ReferenceLine y={0} stroke="#9ca3af" strokeDasharray="3 3" />
            )}
            <Tooltip content={<TimelineTooltip eventDefs={eventDefs} metric={metric} />} />
            <Legend
              wrapperStyle={{ fontSize: 12, cursor: 'pointer' }}
              onClick={(entry) => {
                if (entry?.dataKey) toggleEvent(String(entry.dataKey));
              }}
              formatter={(value) => {
                const def = eventDefs.find((d) => d.event_id === value);
                const label = def?.display_name || def?.event_name || value;
                const hidden = hiddenEvents.has(String(value));
                return (
                  <span style={{ opacity: hidden ? 0.4 : 1, textDecoration: hidden ? 'line-through' : 'none' }}>
                    {label}
                  </span>
                );
              }}
            />
            {eventDefs.map((def) => (
              <Line
                key={def.event_id}
                type="monotone"
                dataKey={def.event_id}
                stroke={def.color}
                strokeWidth={2}
                dot={{ r: 3 }}
                activeDot={{ r: 5 }}
                connectNulls={false}
                hide={hiddenEvents.has(def.event_id)}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </Card.Body>
    </Card>
  );
}

interface TooltipPayloadEntry {
  dataKey: string;
  value: number;
  color: string;
}

function TimelineTooltip({
  active,
  payload,
  label,
  eventDefs,
  metric,
}: {
  active?: boolean;
  payload?: TooltipPayloadEntry[];
  label?: string;
  eventDefs: Array<{ event_id: string; event_name: string; display_name?: string; color: string }>;
  metric: Metric;
}) {
  if (!active || !payload || !payload.length) return null;

  const visible = payload.filter((p) => p.value != null);
  const sorted = [...visible].sort((a, b) => b.value - a.value);
  const fmt = (v: number) => (metric === 'conversion_lift' ? `${v >= 0 ? '+' : ''}${v}%` : `${v}`);

  return (
    <div className="rounded-md border bg-background p-2 shadow-sm text-xs">
      <p className="font-medium mb-1">{label}</p>
      <div className="space-y-0.5">
        {sorted.map((entry) => {
          const def = eventDefs.find((d) => d.event_id === entry.dataKey);
          const name = def?.display_name || def?.event_name || entry.dataKey;
          return (
            <div key={entry.dataKey} className="flex items-center gap-2">
              <span
                className="inline-block h-2 w-2 rounded-full"
                style={{ backgroundColor: entry.color }}
              />
              <span className="text-muted-foreground">{name}</span>
              <span className="ml-auto font-medium tabular-nums">{fmt(entry.value)}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default CustomEventsTimeline;
